import { pushApi, type PushPublicKey } from '@core/api/push';

export { pushApi };

/**
 * Уведомления в браузере (web-push) — только во входе PWA:
 *   - в мини-аппе Telegram не показываем: там уведомления приходят в Telegram;
 *   - на iPhone/iPad push есть только у PWA, установленной на экран «Домой» (iOS 16.4+): в Safari — подсказка;
 *   - разрешение спрашиваем только по нажатию клиента («Включить»), не при открытии приложения.
 * Одна подписка браузера (один сайт PWA на все заведения) может быть привязана к нескольким карточкам клиента
 * (по заведениям); «выключить» снимает привязку только своей карточки, подписку браузера не трогает.
 */

export type PushAvailability =
  | 'off' // вход без web-push (мини-апп Telegram) — ничего не показываем
  | 'unsupported' // браузер не умеет push
  | 'ios-install' // iPhone/iPad, приложение открыто в браузере, а не с экрана «Домой»
  | 'denied' // запрещено в настройках браузера
  | 'available';

export interface PushEnv {
  /** Вход разрешает web-push (capabilities.webPush: PWA — да, мини-апп — нет). */
  entryAllows: boolean;
  insideTelegram: boolean;
  hasServiceWorker: boolean;
  hasPushManager: boolean;
  hasNotification: boolean;
  permission: NotificationPermission | null;
  isIOS: boolean;
  /** Открыто как установленное приложение (display-mode: standalone / navigator.standalone). */
  standalone: boolean;
}

export function pushAvailability(env: PushEnv): PushAvailability {
  if (!env.entryAllows || env.insideTelegram) return 'off';
  if (env.isIOS && !env.standalone) return 'ios-install';
  if (!env.hasServiceWorker || !env.hasPushManager || !env.hasNotification) return 'unsupported';
  if (env.permission === 'denied') return 'denied';
  return 'available';
}

/** Среда текущего браузера. */
export function readPushEnv(entryAllows: boolean): PushEnv {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return {
      entryAllows,
      insideTelegram: false,
      hasServiceWorker: false,
      hasPushManager: false,
      hasNotification: false,
      permission: null,
      isIOS: false,
      standalone: false,
    };
  }
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const standalone =
    (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches) ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  const hasNotification = 'Notification' in window;
  return {
    entryAllows,
    insideTelegram: !!window.Telegram?.WebApp?.initData,
    hasServiceWorker: 'serviceWorker' in navigator,
    hasPushManager: 'PushManager' in window,
    hasNotification,
    permission: hasNotification ? Notification.permission : null,
    isIOS,
    standalone,
  };
}

/** VAPID-ключ сервера (base64url) → байты для pushManager.subscribe. */
export function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

type KV = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** Зависимости от браузера — подменяются в тестах. */
export interface PushDeps {
  registration(): Promise<ServiceWorkerRegistration | null>;
  permission(): NotificationPermission;
  requestPermission(): Promise<NotificationPermission>;
  serverKey(): Promise<PushPublicKey>;
  storage: KV;
}

function safeStorage(): KV {
  try {
    if (typeof localStorage !== 'undefined') return localStorage;
  } catch {
    // недоступен (приватный режим) — без памяти между запусками
  }
  const m = new Map<string, string>();
  return {
    getItem: (k) => m.get(k) ?? null,
    setItem: (k, v) => void m.set(k, v),
    removeItem: (k) => void m.delete(k),
  };
}

export function browserPushDeps(): PushDeps {
  return {
    registration: async () => {
      if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
      // Воркер PWA регистрирует vite-plugin-pwa; ждём не дольше 10 с, чтобы кнопка не зависла.
      return Promise.race([
        navigator.serviceWorker.ready,
        new Promise<null>((resolve) => setTimeout(() => resolve(null), 10_000)),
      ]);
    },
    permission: () => (typeof Notification !== 'undefined' ? Notification.permission : 'denied'),
    requestPermission: () => Notification.requestPermission(),
    serverKey: () => pushApi.publicKey(),
    storage: safeStorage(),
  };
}

const storeKey = (userId: string) => `lunit:webpush:${userId}`;

function sameKey(a: ArrayBuffer | null | undefined, b: Uint8Array): boolean {
  if (!a) return false;
  const x = new Uint8Array(a);
  return x.length === b.length && x.every((v, i) => v === b[i]);
}

function toBody(sub: PushSubscription) {
  const j = sub.toJSON();
  return { endpoint: j.endpoint ?? sub.endpoint, keys: { p256dh: j.keys?.p256dh ?? '', auth: j.keys?.auth ?? '' } };
}

export type EnableResult = 'subscribed' | 'denied' | 'disabled' | 'unsupported';

/** Включить уведомления карточке userId в этом браузере. Вызывать по нажатию клиента (жест нужен iOS/Safari). */
export async function enableWebPush(userId: string, deps: PushDeps = browserPushDeps()): Promise<EnableResult> {
  const cfg = await deps.serverKey();
  if (!cfg.enabled || !cfg.publicKey) return 'disabled';
  const perm = deps.permission() === 'granted' ? 'granted' : await deps.requestPermission();
  if (perm !== 'granted') return 'denied';
  const reg = await deps.registration();
  if (!reg) return 'unsupported';
  const key = urlBase64ToUint8Array(cfg.publicKey);
  let sub = await reg.pushManager.getSubscription();
  if (sub && !sameKey(sub.options?.applicationServerKey, key)) {
    // Ключи сервера сменились — старая подписка больше не принимается push-сервисом.
    await sub.unsubscribe();
    sub = null;
  }
  if (!sub) {
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
  }
  await pushApi.subscribe(toBody(sub));
  deps.storage.setItem(storeKey(userId), sub.endpoint);
  return 'subscribed';
}

/** Выключить уведомления карточке userId в этом браузере (на сервере — только её привязка). */
export async function disableWebPush(userId: string, deps: PushDeps = browserPushDeps()): Promise<void> {
  // Сохранённый адрес подписки (обновляется при каждом запуске) — без ожидания воркера; нет — спросим браузер.
  let endpoint = deps.storage.getItem(storeKey(userId));
  if (!endpoint) {
    const reg = await deps.registration();
    endpoint = (reg ? await reg.pushManager.getSubscription() : null)?.endpoint ?? null;
  }
  if (endpoint) {
    await pushApi.unsubscribe(endpoint);
  }
  deps.storage.removeItem(storeKey(userId));
}

/** Включены ли уведомления карточке userId в этом браузере. */
export async function isWebPushOn(userId: string, deps: PushDeps = browserPushDeps()): Promise<boolean> {
  const stored = deps.storage.getItem(storeKey(userId));
  if (!stored || deps.permission() !== 'granted') return false;
  const reg = await deps.registration();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  return !!sub;
}

/** При запуске (клиент вошёл): включённую подписку досылаем на сервер — без запроса разрешения. Браузер мог
 * обновить подписку, сервер — снять протухшую. Ошибки не показываем. */
export async function resyncWebPush(userId: string, deps: PushDeps = browserPushDeps()): Promise<void> {
  if (!deps.storage.getItem(storeKey(userId)) || deps.permission() !== 'granted') return;
  try {
    await enableWebPush(userId, deps);
  } catch {
    // сеть/сервер — повторим при следующем запуске
  }
}

/** Выход из PWA: снять привязку карточки на сервере, пока сессия жива (общее устройство). Ошибки не мешают выходу. */
export async function forgetWebPushOnLogout(userId: string | null, deps: PushDeps = browserPushDeps()): Promise<void> {
  if (!userId || !deps.storage.getItem(storeKey(userId))) return;
  try {
    await disableWebPush(userId, deps);
  } catch {
    deps.storage.removeItem(storeKey(userId));
  }
}

const PROMPT_DISMISS_FOR_MS = 30 * 24 * 60 * 60 * 1000;
export const PUSH_PROMPT_DISMISS_KEY = 'lunit:webpush:prompt-dismissed-at';

/** Карточка-приглашение на главной (после входа): 'enable' — «Включить» (системное окно — только по нажатию),
 * 'ios-install' — подсказка установить приложение на экран «Домой», null — не показывать. Скрытая крестиком —
 * на 30 дней. */
export function shouldShowPushPrompt(s: {
  availability: PushAvailability;
  serverEnabled: boolean | null;
  on: boolean;
  permission: NotificationPermission | null;
  dismissedAt: number | null;
  now: number;
}): 'enable' | 'ios-install' | null {
  if (s.serverEnabled !== true || s.on) return null;
  if (s.dismissedAt && s.now - s.dismissedAt < PROMPT_DISMISS_FOR_MS) return null;
  if (s.availability === 'ios-install') return 'ios-install';
  if (s.availability === 'available' && s.permission !== 'denied') return 'enable';
  return null;
}
